/**
 * Client-side mirror of /app/backend/new_lead_sla.py — computes
 * business-hours elapsed since a UTC assignment timestamp using
 * Asia/Dubai working hours (Mon–Fri 09:00–20:00).
 *
 * Used by:
 *   • LeadCard countdown chip (SalesCRMPage)
 *   • SlaWatchdogPanel (dashboards)
 *
 * The rules MUST match the backend exactly, otherwise the chip will lie.
 */

export const SLA_FEATURE_START_UTC = '2026-07-02T20:00:00+00:00'; // Dubai midnight July 3
export const SLA_WARN_HOURS = 3;
export const SLA_REASSIGN_HOURS = 12;

const DUBAI_OFFSET_MIN = 4 * 60; // UAE is UTC+4, no DST
const BIZ_START_HOUR = 9;
const BIZ_END_HOUR = 20;
const BIZ_DAYS = new Set([1, 2, 3, 4, 5]); // Mon–Fri (getDay: Sun=0)

/** UTC datetime → Dubai wall-clock parts */
function toDubaiParts(utcDate) {
  const d = new Date(utcDate.getTime() + DUBAI_OFFSET_MIN * 60_000);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    dayOfWeek: d.getUTCDay(), // Sun=0 .. Sat=6
    utc: d.getTime(),
  };
}

/** Dubai wall-clock → UTC ms */
function dubaiToUtcMs(y, m, d, h, mm) {
  return Date.UTC(y, m, d, h, mm) - DUBAI_OFFSET_MIN * 60_000;
}

/**
 * Business hours between two Date objects (UTC). Mirrors the Python
 * _business_hours_between exactly.
 */
export function businessHoursBetween(startUtc, endUtc) {
  if (!(startUtc instanceof Date) || !(endUtc instanceof Date)) return 0;
  if (endUtc <= startUtc) return 0;

  let totalSec = 0;
  let cursorUtcMs = startUtc.getTime();
  const endMs = endUtc.getTime();

  // Safety guard — walk at most 60 days
  let iters = 0;
  while (cursorUtcMs < endMs && iters < 60) {
    iters += 1;
    const p = toDubaiParts(new Date(cursorUtcMs));
    const dayStartMs = dubaiToUtcMs(p.year, p.month, p.day, BIZ_START_HOUR, 0);
    const dayEndMs = dubaiToUtcMs(p.year, p.month, p.day, BIZ_END_HOUR, 0);
    if (BIZ_DAYS.has(p.dayOfWeek)) {
      const winStart = Math.max(cursorUtcMs, dayStartMs);
      const winEnd = Math.min(endMs, dayEndMs);
      if (winEnd > winStart) totalSec += (winEnd - winStart) / 1000;
    }
    // Jump to next day 09:00 Dubai
    cursorUtcMs = dubaiToUtcMs(p.year, p.month, p.day + 1, BIZ_START_HOUR, 0);
  }
  return totalSec / 3600;
}

/**
 * Predict when a given (currently zero-time) accumulated biz-hour target
 * will be reached, starting from `fromUtc`. Returns { targetUtc: Date } or
 * null if `fromUtc` is invalid.
 */
export function whenBizHoursElapsed(fromUtc, hoursNeeded) {
  if (!(fromUtc instanceof Date)) return null;
  if (hoursNeeded <= 0) return { targetUtc: fromUtc };

  let secLeft = hoursNeeded * 3600;
  let cursorUtcMs = fromUtc.getTime();

  let iters = 0;
  while (secLeft > 0 && iters < 60) {
    iters += 1;
    const p = toDubaiParts(new Date(cursorUtcMs));
    const dayStartMs = dubaiToUtcMs(p.year, p.month, p.day, BIZ_START_HOUR, 0);
    const dayEndMs = dubaiToUtcMs(p.year, p.month, p.day, BIZ_END_HOUR, 0);

    if (BIZ_DAYS.has(p.dayOfWeek)) {
      const winStart = Math.max(cursorUtcMs, dayStartMs);
      const winEnd = dayEndMs;
      const winSec = Math.max(0, (winEnd - winStart) / 1000);
      if (winSec >= secLeft) {
        return { targetUtc: new Date(winStart + secLeft * 1000) };
      }
      secLeft -= winSec;
    }
    cursorUtcMs = dubaiToUtcMs(p.year, p.month, p.day + 1, BIZ_START_HOUR, 0);
  }
  return null;
}

/**
 * Format a duration in business-hours as "Xh Ym" or "Xm".
 */
export function formatBizDuration(hours) {
  if (hours == null || !isFinite(hours)) return '—';
  if (hours < 0) hours = 0;
  const totalMinutes = Math.round(hours * 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Snapshot the SLA state of a new_lead based on the lead document.
 * Returns { applies, elapsed, remainingToWarn, remainingToReassign,
 *           targetWarnAt, targetReassignAt, level } where level ∈
 *   'ok' | 'approaching_warn' | 'warned' | 'approaching_reassign' | 'breached'
 *
 * Only applies to leads where stage=='new_lead' and assigned_at >= FEATURE_START.
 */
export function computeSlaState(lead, nowUtc = new Date()) {
  const state = {
    applies: false,
    elapsedBizHours: 0,
    remainingToWarn: null,
    remainingToReassign: null,
    targetWarnAt: null,
    targetReassignAt: null,
    warned: !!lead?.new_lead_sla_3h_warned,
    reassigned: !!lead?.new_lead_sla_12h_reassigned,
    level: 'ok',
  };

  if (!lead || lead.stage !== 'new_lead') return state;
  if (lead.new_lead_sla_exempt) return state; // TL holding a batch for manual distribution
  const assignedIso = lead.assigned_at;
  if (!assignedIso) return state;
  const assignedUtc = new Date(assignedIso);
  const featureStartUtc = new Date(SLA_FEATURE_START_UTC);
  if (assignedUtc < featureStartUtc) return state;

  state.applies = true;
  state.elapsedBizHours = businessHoursBetween(assignedUtc, nowUtc);

  const warnHit = whenBizHoursElapsed(assignedUtc, SLA_WARN_HOURS);
  const reassignHit = whenBizHoursElapsed(assignedUtc, SLA_REASSIGN_HOURS);
  state.targetWarnAt = warnHit?.targetUtc || null;
  state.targetReassignAt = reassignHit?.targetUtc || null;

  if (state.reassigned) {
    state.level = 'breached';
  } else if (state.elapsedBizHours >= SLA_REASSIGN_HOURS) {
    state.level = 'breached';
  } else if (state.warned || state.elapsedBizHours >= SLA_WARN_HOURS) {
    // Warned; how close to reassignment?
    const remaining = Math.max(0, SLA_REASSIGN_HOURS - state.elapsedBizHours);
    state.remainingToReassign = remaining;
    state.level = remaining <= 3 ? 'approaching_reassign' : 'warned';
  } else {
    const remaining = Math.max(0, SLA_WARN_HOURS - state.elapsedBizHours);
    state.remainingToWarn = remaining;
    state.level = remaining <= 0.5 ? 'approaching_warn' : 'ok';
  }
  return state;
}
